// Arendă calculations. The contract is 1000 kg of wheat per hectare; every other product is
// worth the same money at its own price. These are the formulas the page has always used.
import { contractExpiry, toNumber } from './ui';

const KG_PER_HA = 1000;

// Each company can start its math from its own kg of wheat per hectare.
export const kgPerHa = (rates) => parseFloat(rates?.baseKg) || KG_PER_HA;

export const DEFAULT_TYPES = [
  { id: 'grau', name: 'Grâu', unit: 'kg', unitType: 'kg' },
  { id: 'money', name: 'Bani', unit: 'lei', unitType: 'lei' },
  { id: 'ulei', name: 'Ulei', unit: 'litri', unitType: 'l' },
  { id: 'zahar', name: 'Zahăr', unit: 'kg', unitType: 'kg' },
  { id: 'faina', name: 'Făină', unit: 'kg', unitType: 'kg' },
];
export const DEFAULT_RATES = { grau: 1, money: 1, ulei: 5, zahar: 3, faina: 2 };

// Saved records can hold product keys that are no longer in the list, including old English ones.
const PRODUCT_NAMES = {
  grau: 'Grâu', money: 'Bani', ulei: 'Ulei', zahar: 'Zahăr', faina: 'Făină', porumb: 'Porumb',
  oil: 'Ulei', sugar: 'Zahăr', flour: 'Făină', wheat: 'Grâu', corn: 'Porumb',
};
const PRODUCT_UNITS = { money: 'lei', ulei: 'l', oil: 'l' };

export const productName = (id, types = []) =>
  types.find((type) => type.id === id)?.name || PRODUCT_NAMES[id] || id.charAt(0).toUpperCase() + id.slice(1);
export const productUnit = (id, types = []) =>
  types.find((type) => type.id === id)?.unitType || PRODUCT_UNITS[id] || 'kg';

// A company's own prices and products. Until it has saved its own, it uses the farm's general ones.
export const termsFor = (company, companyTerms, generalRates, generalTypes) => {
  const saved = company ? companyTerms?.[company] : null;
  return saved
    ? { rates: correctRates(saved.rates || {}), types: saved.types || generalTypes, own: true }
    : { rates: generalRates, types: generalTypes, own: false };
};

// Every product any company uses, so old records and receipts can always name theirs.
export const allTypesOf = (companyTerms, generalTypes) => {
  const byId = new Map();
  [...generalTypes, ...Object.values(companyTerms || {}).flatMap((terms) => terms.types || [])].forEach((type) => {
    if (!byId.has(type.id)) byId.set(type.id, type);
  });
  return [...byId.values()];
};

export const correctRates = (saved) => ({ grau: saved.grau || 1, ulei: saved.ulei || 5, zahar: saved.zahar || 3, faina: saved.faina || 2, ...saved, money: 1 });

export const valuePerHa = (rates) => kgPerHa(rates) * (rates?.grau || 1);
export const budgetFor = (hectares, rates) => Math.round(valuePerHa(rates) * hectares);
export const priceOf = (id, rates) => (id === 'money' ? 1 : toNumber(rates?.[id]));
export const hasPrice = (id, rates) => priceOf(id, rates) > 0;

const quantityPerHa = (id, rates) => {
  if (id === 'grau') return kgPerHa(rates);
  if (id === 'money') return valuePerHa(rates);
  return valuePerHa(rates) / priceOf(id, rates);
};
export const perHaLabel = (id, rates) => (hasPrice(id, rates) ? Math.round(quantityPerHa(id, rates)) : 0);

const valueOfQuantity = (id, quantity, rates) => (id === 'money' ? quantity : Math.round(quantity * priceOf(id, rates)));

// Each returns the three linked numbers of a product line, from whichever one was typed.
export const fromSurface = (id, typed, rates) => {
  const amount = Math.round(toNumber(typed) * quantityPerHa(id, rates));
  return { surface: typed, amount, value: valueOfQuantity(id, amount, rates) };
};
export const fromQuantity = (id, typed, rates) => {
  const amount = parseInt(typed, 10) || 0;
  return { surface: (amount / quantityPerHa(id, rates)).toFixed(2), amount, value: valueOfQuantity(id, amount, rates) };
};
export const fromValue = (id, typed, rates) => {
  const value = parseInt(typed, 10) || 0;
  const amount = id === 'money' ? value : Math.round(value / priceOf(id, rates));
  return { surface: (amount / quantityPerHa(id, rates)).toFixed(2), amount, value };
};

export const sumValues = (values) => Object.values(values || {}).reduce((sum, value) => sum + (parseInt(value, 10) || 0), 0);
export const sumSurfaces = (surfaces) => Object.values(surfaces || {}).reduce((sum, value) => sum + toNumber(value), 0);

// The most of one product that still fits, given what the other products already use.
export const maxQuantity = (id, session, rates) => {
  const usedElsewhere = sumValues({ ...session.values, [id]: 0 });
  const left = Math.max(0, budgetFor(session.person.totalSuprafata, rates) - usedElsewhere);
  return id === 'money' ? left : Math.floor(left / priceOf(id, rates));
};

// How much one tap of + or - changes: about 50 lei, rounded to a number that is easy to count.
export const stepFor = (id, rates) => {
  const target = 50 / priceOf(id, rates);
  return [100, 50, 20, 10, 5, 2].find((step) => step <= target) || 1;
};

export const buildPeople = (parcels) => {
  const byCnp = new Map();
  parcels.forEach((parcel) => {
    if (!parcel?.cnp) return;
    if (!byCnp.has(parcel.cnp)) {
      byCnp.set(parcel.cnp, {
        nume: parcel.nume || 'N/A',
        cnp: parcel.cnp,
        seria: parcel.seria || 'N/A',
        numarulBuletinului: parcel.numarulBuletinului || 'N/A',
        numarContract: parcel.numarContract || '',
        dataContract: parcel.dataContract || 'N/A',
        domiciliat: parcel.domiciliat || 'N/A',
        dataNasterii: parcel.dataNasterii || 'N/A',
        firma: parcel.firma || '',
        contractExpiration: contractExpiry(parcel.dataContract),
        parcels: [],
        totalSuprafata: 0,
      });
    }
    const person = byCnp.get(parcel.cnp);
    person.parcels.push(parcel);
    person.totalSuprafata += toNumber(parcel.suprafata);
  });

  return [...byCnp.values()].map((person) => ({
    ...person,
    ...mainCompany(person.parcels),
    totalSuprafata: Math.round(person.totalSuprafata * 10000) / 10000, // no 1.2200000000000002
    tGroups: groupT(person.parcels),
  }));
};

// A person belongs to one section: the company where they have the most land.
const mainCompany = (parcels) => {
  const areas = new Map();
  parcels.forEach((parcel) => {
    const name = parcel.firma || '';
    areas.set(name, (areas.get(name) || 0) + toNumber(parcel.suprafata));
  });
  const firms = [...areas.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name);
  return { firma: firms[0] || '', otherFirms: firms.slice(1).filter(Boolean) };
};

// T numbers only mean something inside their comună, so they are always kept together with it.
export const groupT = (parcels) => {
  const byComuna = new Map();
  parcels.forEach((parcel) => {
    if (parcel.t === undefined || parcel.t === null || parcel.t === '') return;
    const comuna = parcel.locatie && parcel.locatie !== 'N/A' ? parcel.locatie : 'Fără comună';
    if (!byComuna.has(comuna)) byComuna.set(comuna, new Set());
    byComuna.get(comuna).add(Number(parcel.t));
  });
  return [...byComuna.entries()]
    .map(([comuna, numbers]) => ({ comuna, numbers: [...numbers].sort((a, b) => a - b) }))
    .sort((a, b) => a.comuna.localeCompare(b.comuna, 'ro'));
};

export const isExpired = (person) => Boolean(person.contractExpiration && person.contractExpiration <= new Date());
export const isExpiringSoon = (person) => {
  if (!person.contractExpiration) return false;
  const limit = new Date();
  limit.setFullYear(limit.getFullYear() + 2);
  return person.contractExpiration <= limit;
};

const plain = (text) => String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const matchesSearch = (person, search) => {
  const term = plain(search).trim();
  if (!term) return true;
  const surface = term.replace(',', '.');
  return plain(person.nume).includes(term) || String(person.cnp).includes(term)
    || String(person.totalSuprafata).includes(surface) || toNumber(person.totalSuprafata).toFixed(2).includes(surface);
};

export const sortPeople = (people, sortBy) => [...people].sort((a, b) => {
  if (sortBy === 'suprafata_desc') return b.totalSuprafata - a.totalSuprafata;
  if (sortBy === 'suprafata_asc') return a.totalSuprafata - b.totalSuprafata;
  if (sortBy === 'contract_expiry') return (a.contractExpiration || new Date('2099-12-31')) - (b.contractExpiration || new Date('2099-12-31'));
  return a.nume.localeCompare(b.nume, 'ro');
});

// Old records keep their numbers directly on the record instead of inside distributionData.
export const distributionOf = (record) => {
  const saved = record.distributionData;
  return {
    surfaces: saved?.surfaces || record.surfaces || {},
    amounts: saved?.amounts || record.amounts || {},
    values: saved?.values || record.values || {},
    delayedPickup: saved?.delayedPickup || {},
    pickedUpBy: saved?.pickedUpBy || null,
  };
};

export const recordTotal = (record) => record.totalValue ?? sumValues(distributionOf(record).values);

export const startSession = (person, record, rates) => {
  const session = {
    person, record: record || null, surfaces: {}, amounts: {}, values: {}, delayed: {},
    pickedUp: false, other: { nume: '', cnp: '' }, changed: false,
  };
  if (!record) return session;

  const saved = distributionOf(record);
  session.amounts = { ...saved.amounts };
  session.values = { ...saved.values };
  session.delayed = { ...saved.delayedPickup };
  session.surfaces = { ...saved.surfaces };
  if (!record.distributionData) {
    Object.entries(saved.amounts).forEach(([id, amount]) => {
      if (amount > 0 && hasPrice(id, rates)) session.surfaces[id] = (amount / quantityPerHa(id, rates)).toFixed(2);
    });
  }
  if (saved.pickedUpBy) {
    session.pickedUp = true;
    session.other = { ...saved.pickedUpBy };
  }
  return session;
};

// Same saved shape as always. Changing a distribution keeps its id and the date it was picked up.
export const buildRecord = (session, year, now = new Date().toISOString()) => {
  const { person, record } = session;
  return {
    id: record?.id || `${person.cnp}_${Date.now()}`,
    cnp: person.cnp,
    nume: person.nume,
    totalSuprafata: person.totalSuprafata,
    year,
    distributionData: {
      surfaces: session.surfaces,
      amounts: session.amounts,
      values: session.values,
      delayedPickup: session.delayed,
      pickedUpBy: session.pickedUp ? { nume: session.other.nume.trim(), cnp: session.other.cnp.trim() } : null,
    },
    timestamp: record?.timestamp || now,
    takenTimestamp: record?.takenTimestamp || now,
    ...(record ? { lastEdited: now } : {}),
    status: 'completed',
    hectares: person.totalSuprafata,
    amounts: session.amounts,
    values: session.values,
    totalValue: sumValues(session.values),
  };
};

const sameRecord = (a, b) => (a.id && b.id ? a.id === b.id : a.cnp === b.cnp && Number(a.year) === Number(b.year));

// Returns the new list, or null when another device already saved this person for this year.
export const applyRecord = (records, record, isEdit) => {
  if (isEdit) {
    return records.some((item) => sameRecord(item, record))
      ? records.map((item) => (sameRecord(item, record) ? record : item))
      : [...records, record];
  }
  const taken = records.some((item) => item.cnp === record.cnp && Number(item.year) === Number(record.year));
  return taken ? null : [...records, record];
};

export const canFinish = (session, rates) => {
  const total = sumValues(session.values);
  if (total === 0 || total > budgetFor(session.person.totalSuprafata, rates)) return false;
  return !session.pickedUp || (Boolean(session.other.nume?.trim()) && session.other.cnp?.trim().length === 13);
};

export const yearStatistics = (people, yearRecords, rates, types) => {
  const receivedCnps = new Set(yearRecords.map((record) => record.cnp));
  const received = people.filter((person) => receivedCnps.has(person.cnp)).length;
  const distributed = yearRecords.reduce((sum, record) => sum + recordTotal(record), 0);
  const ratesOf = typeof rates === 'function' ? rates : () => rates;
  const possible = people.reduce((sum, person) => sum + budgetFor(person.totalSuprafata, ratesOf(person)), 0);

  const amounts = {};
  yearRecords.forEach((record) => {
    Object.entries(distributionOf(record).amounts).forEach(([id, amount]) => {
      amounts[id] = (amounts[id] || 0) + (Number(amount) || 0);
    });
  });
  const products = Object.entries(amounts)
    .filter(([, amount]) => amount > 0)
    .map(([id, amount]) => ({ id, name: productName(id, types), unit: productUnit(id, types), amount }));

  return {
    total: people.length,
    received,
    waiting: people.length - received,
    percent: people.length ? Math.round((received / people.length) * 100) : 0,
    distributed,
    possible,
    remaining: Math.max(0, possible - distributed),
    products,
  };
};
